import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Box, Button, Checkbox, Form } from '@alto-avios/alto-ui'
import { DatePicker, DatePickerValues } from '@reward-platform/lift/components'
import { Controller, FieldError, useForm } from 'react-hook-form'
import {
  FlightSearchFormData,
  FlightSearchFormSchema,
  TravellersFormData,
} from '@reward-platform/flights-schemas'
import { ZodIssueCode } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { defaultTravellerQuantity } from '~/components/search-and-results/FlightTravellerInput/constants'
import useGetAirport from '~/hooks/useGetAirport/useGetAirport'
import { FlightTravellerInput } from '~/components/search-and-results/FlightTravellerInput'
import { usePlatformConfig } from '~/context/platform'
import { addDays } from 'date-fns'
import { useFormValidationMessages } from '~/hooks/useMessages'
import { useLocalisation } from '~/context/localisation'
import { isClient } from '~/utils/envChecks'
import { SearchBox } from './SearchBox'
import styles from './SearchFlightForm.module.scss'
import { SearchFieldSwapButton } from '../SearchFieldSwapButton'
import { useValidRouteQuery } from './hooks/useValidRouteQuery'
import { validatePassengers } from '../FlightTravellerInput/FlightTravellerInput.utils'
import { focusSearchBox } from './SearchBoxUtils'

export type DatePickerControlRequest = {
  action: 'open' | 'close'
  requestId: number
}

interface SearchFlightFormProps {
  onValidSubmit?: (data: Readonly<FlightSearchFormData>) => void
  submitText?: string
  formData?: Partial<FlightSearchFormData>
  datePickerControlRequest?: DatePickerControlRequest | null
  destinationFocusRequest?: number | null
}

const defaultTravellerInput: TravellersFormData = {
  ...defaultTravellerQuantity,
  childrenAges: [],
}

const defaultFormData: Partial<FlightSearchFormData> = {
  travellers: defaultTravellerInput,
  oneWayOnly: false,
}

export function SearchFlightForm({
  onValidSubmit,
  submitText,
  formData,
  datePickerControlRequest,
  destinationFocusRequest,
}: Readonly<SearchFlightFormProps>) {
  useEffect(() => {
    if (!destinationFocusRequest) {
      return undefined
    }

    if (isClient()) {
      const destinationField = document.getElementById('toLocation')
      destinationField?.scrollIntoView?.({ block: 'center' })

      const focusRequest = globalThis.requestAnimationFrame(() => {
        focusSearchBox('toLocation', { preventScroll: true })
      })

      return () => {
        globalThis.cancelAnimationFrame(focusRequest)
      }
    }

    return undefined
  }, [destinationFocusRequest])

  const { locale } = useLocalisation()
  const { opCo: partnerId } = usePlatformConfig()

  const {
    reset,
    setValue,
    getValues,
    control,
    resetField,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FlightSearchFormData>({
    resolver: zodResolver(FlightSearchFormSchema),
    defaultValues: defaultFormData,
  })

  const fromLocation = watch('fromLocation')
  const toLocation = watch('toLocation')

  const fromAirportData = useGetAirport(fromLocation?.airportCode)
  const toAirportData = useGetAirport(toLocation?.airportCode)

  const lastHydratedFormData = useRef<Partial<FlightSearchFormData> | null>(null)

  useEffect(() => {
    if (!formData || isSubmitting || formData === lastHydratedFormData.current) {
      return
    }

    lastHydratedFormData.current = formData

    reset()
    clearErrors()

    const {
      dates,
      toLocation: toLocationParam,
      fromLocation: fromLocationParam,
      oneWayOnly,
      travellers: travellersParam,
    } = formData

    if (dates) {
      setValue('dates', dates)
    }
    if (fromLocationParam) {
      setValue('fromLocation', fromLocationParam)
    }
    if (toLocationParam) {
      setValue('toLocation', toLocationParam)
    }
    if (oneWayOnly !== undefined) {
      setValue('oneWayOnly', oneWayOnly)

      if (oneWayOnly) {
        resetField('dates.endDateTime')
      }
    }
    if (travellersParam) {
      setValue('travellers', travellersParam)
    }
  }, [isSubmitting, reset, resetField, formData, setValue, clearErrors])

  const hasErrors = Object.keys(errors).length > 0

  const onSubmit = async (data: FlightSearchFormData) => {
    const { travellers, dates, oneWayOnly } = data
    const travellersErrors = validatePassengers(travellers)

    if (travellersErrors) {
      setError('travellers', { type: 'travellers', message: travellersErrors })
      return
    }

    if (!oneWayOnly && !dates.endDateTime) {
      setError('dates', { type: 'endDateTime' })
      return
    }
    onValidSubmit?.(data)
  }

  // EI does not provide availability >330 days into the future
  // Customers cannot book flights within 24 hours

  const { minDate, maxDate } = useMemo(
    () => ({
      minDate: addDays(new Date(), 1),
      maxDate: addDays(new Date(), 330),
    }),
    []
  )

  const swapLocations = useCallback(() => {
    if (toAirportData && fromAirportData) {
      setValue('fromLocation', toAirportData)
      setValue('toLocation', fromAirportData)
      clearErrors(['fromLocation', 'toLocation'])
    }
  }, [setValue, toAirportData, fromAirportData, clearErrors])

  const t = useTranslations('pages.flights.search_form')
  const { mustBeSelected, getValidationErrorMessage } = useFormValidationMessages()

  const errorMap = useMemo(
    () => ({
      fromLocation: { [ZodIssueCode.invalid_type]: mustBeSelected(t('departureAirport')) },
      toLocation: { [ZodIssueCode.invalid_type]: mustBeSelected(t('arrivalAirport')) },
      dates: {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        endDateTime: mustBeSelected(t('return-date')),
        // eslint-disable-next-line sonarjs/no-duplicate-string
        [ZodIssueCode.invalid_type]: mustBeSelected(t('departure-date')),
      },
      startDateTime: {
        [ZodIssueCode.invalid_type]: mustBeSelected(t('departure-date')),
      },
      endDateTime: {
        [ZodIssueCode.invalid_type]: mustBeSelected(t('return-date')),
      },
    }),
    [mustBeSelected, t]
  )

  const getFieldError = useCallback(
    (field: keyof Partial<FlightSearchFormData>) => {
      return (
        errors?.[field] &&
        getValidationErrorMessage({
          errorMap,
          errors,
          field,
        })
      )
    },
    [errorMap, errors, getValidationErrorMessage]
  )

  const getRangeDateFieldError = (field: keyof DatePickerValues) => {
    // handle the top level dates error
    if (
      errors?.dates?.type === field ||
      (field === 'startDateTime' && errors?.dates?.type === ZodIssueCode.invalid_type)
    ) {
      return getFieldError('dates')
    }

    const oneWay = getValues('oneWayOnly')

    // force required error for return date since it's optional in zod
    if (!oneWay && field === 'endDateTime' && errors?.dates?.type === ZodIssueCode.invalid_type) {
      return errorMap.endDateTime[ZodIssueCode.invalid_type]
    }

    // handle the nested dates error
    const datesErrors = errors.dates as Record<keyof DatePickerValues, FieldError>
    return (
      datesErrors?.[field] &&
      getValidationErrorMessage({
        errorMap,
        errors: datesErrors,
        field,
      })
    )
  }

  const { isRouteValid, isValidatingRoute } = useValidRouteQuery(
    partnerId,
    fromLocation?.airportCode,
    toLocation?.airportCode
  )

  useEffect(() => {
    if (isRouteValid !== undefined && !isRouteValid) {
      resetField('toLocation')
    }
  }, [isRouteValid, resetField])

  return (
    <Form
      className={styles.container}
      onSubmit={handleSubmit(onSubmit)}
      data-testid="search-flights-form"
      maxWidth="fullWidth"
    >
      <Box className={styles.route}>
        <section className={styles.airports}>
          <Controller
            control={control}
            name="fromLocation"
            defaultValue={fromAirportData}
            render={({ field: { name, onChange, value } }) => (
              <div id="fromLocation">
                <SearchBox
                  partnerId={partnerId}
                  error={getFieldError(name)}
                  selectedItem={value}
                  onChange={onChange}
                  name={name}
                  label={t('fromLocation')}
                />
              </div>
            )}
          />
          <SearchFieldSwapButton
            onClick={swapLocations}
            label="Swap flight departure and arrival locations"
            disabled={!fromAirportData || !toAirportData || isValidatingRoute}
          />
          <Controller
            control={control}
            name="toLocation"
            defaultValue={toAirportData}
            render={({ field: { name, onChange, value } }) => (
              <div id="toLocation">
                <SearchBox
                  error={getFieldError(name)}
                  partnerId={partnerId}
                  selectedItem={value}
                  onChange={onChange}
                  name={name}
                  label={t('toLocation')}
                  originAirport={fromAirportData?.airportCode}
                />
              </div>
            )}
          />
        </section>
        <Box className={styles.oneWay}>
          <Controller
            control={control}
            name="oneWayOnly"
            render={({ field: { onChange, value } }) => (
              <Checkbox
                id="oneWayOnly"
                name="oneWayOnly"
                value="true"
                isSelected={value}
                onChange={(checked) => {
                  resetField('dates.endDateTime')
                  clearErrors('dates')
                  onChange(checked)
                }}
              >
                {t('one-way-only')}
              </Checkbox>
            )}
          />
        </Box>
      </Box>
      <Box className={styles.datePickers}>
        <Controller
          control={control}
          name="dates"
          render={({ field: { onChange, value, disabled } }) => (
            <DatePicker
              startLabel={t('departure-date')}
              endLabel={t('return-date')}
              values={value}
              isRangePicker
              singleDay={getValues('oneWayOnly')}
              onChange={(newDates) => {
                if (Array.isArray(newDates)) {
                  const [startDate, endDate] = newDates
                  onChange({ startDateTime: startDate, endDateTime: endDate })
                  return
                }
                onChange({ startDateTime: newDates })
              }}
              minDate={minDate}
              maxDate={maxDate}
              locale={locale}
              allowSameDay
              errorStartDate={getRangeDateFieldError('startDateTime')}
              errorEndDate={getRangeDateFieldError('endDateTime')}
              disabled={disabled}
              controlRequest={datePickerControlRequest}
            />
          )}
        />
      </Box>
      <Box className={styles.travelers}>
        <Controller
          control={control}
          name="travellers"
          defaultValue={defaultTravellerInput}
          render={({ field: { name, onChange, value } }) => (
            <FlightTravellerInput name={name} onChange={onChange} value={value} />
          )}
        />
      </Box>
      <Box className={styles.search}>
        <Button type="submit" size="lg" isDisabled={hasErrors || isValidatingRoute} fullWidth>
          {submitText ?? t('search-flights')}
        </Button>
      </Box>
    </Form>
  )
}
